import React from 'react';
import styles from './TxModal.module.css';

export type TxStatus = 'idle' | 'approving' | 'pending' | 'success' | 'error';

interface TxModalProps {
  status: TxStatus;
  txHash?: string;
  errorMsg?: string;
  chainId?: number;
  onClose: () => void;
  title?: string;
}

const EXPLORER_BASE: Record<number, string> = {
  1: 'https://etherscan.io',
  11155111: 'https://sepolia.etherscan.io',
};

export function TxModal({ status, txHash, errorMsg, chainId, onClose, title }: TxModalProps) {
  if (status === 'idle') return null;

  const explorerBase = chainId ? EXPLORER_BASE[chainId] : EXPLORER_BASE[11155111];
  const explorerLink = txHash ? `${explorerBase}/tx/${txHash}` : null;

  return (
    <div className={styles.overlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={styles.modal}>
        <button className={styles.closeBtn} onClick={onClose} aria-label="Close">×</button>

        <div className={styles.iconWrap}>
          {status === 'approving' && <SpinnerIcon className={styles.spinnerBlue} />}
          {status === 'pending' && <SpinnerIcon className={styles.spinnerCyan} />}
          {status === 'success' && <CheckIcon />}
          {status === 'error' && <ErrorIcon />}
        </div>

        <h3 className={styles.modalTitle}>
          {status === 'approving' && 'Approving Token…'}
          {status === 'pending' && (title ?? 'Transaction Pending…')}
          {status === 'success' && 'Transaction Confirmed'}
          {status === 'error' && 'Transaction Failed'}
        </h3>

        {status === 'approving' && (
          <p className={styles.subText}>Approve spending in your wallet, then confirm the swap.</p>
        )}
        {status === 'pending' && (
          <p className={styles.subText}>Waiting for wallet confirmation and block inclusion…</p>
        )}

        {status === 'success' && explorerLink && (
          <a className={styles.explorerLink} href={explorerLink} target="_blank" rel="noopener noreferrer">
            View on Etherscan ↗
          </a>
        )}

        {status === 'error' && errorMsg && (
          <p className={styles.errorText}>{errorMsg}</p>
        )}

        {(status === 'success' || status === 'error') && (
          <button className={styles.doneBtn} onClick={onClose}>
            {status === 'error' ? 'Dismiss' : 'Done'}
          </button>
        )}
      </div>
    </div>
  );
}

function SpinnerIcon({ className }: { className: string }) {
  return (
    <svg className={`${styles.spinner} ${className}`} viewBox="0 0 50 50">
      <circle cx="25" cy="25" r="20" fill="none" strokeWidth="4" strokeLinecap="round" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg className={styles.successIcon} viewBox="0 0 50 50" fill="none">
      <circle cx="25" cy="25" r="22" stroke="currentColor" strokeWidth="3" />
      <path d="M15 25l8 8 13-16" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ErrorIcon() {
  return (
    <svg className={styles.errorIcon} viewBox="0 0 50 50" fill="none">
      <circle cx="25" cy="25" r="22" stroke="currentColor" strokeWidth="3" />
      <path d="M18 18l14 14M32 18L18 32" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}
